import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { labService } from '../../services/LabService';
import {
  FlaskConical,
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Palette,
  Type,
  Layers,
  RotateCcw,
  Keyboard
} from 'lucide-react';


const LabReviewPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Queue state
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);
  const [stats, setStats] = useState({ approved: 0, rejected: 0, skipped: 0 });
  const [direction, setDirection] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    if (!currentUser?.is_admin) {
      navigate('/contents');
      return;
    }
    loadQueue();
  }, [currentUser, navigate]);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const response = await labService.getReviewQueue(100);
      setQueue(response.data || []);
    } catch (err) {
      console.error('Error loading review queue:', err);
    } finally {
      setLoading(false);
    }
  };

  const current = queue[currentIndex];
  const isFinished = started && currentIndex >= queue.length;
  const totalReviewed = stats.approved + stats.rejected + stats.skipped;

  const handleAction = useCallback(async (status) => {
    if (submitting || !current) return;
    setSubmitting(true);
    setDirection(status === 'approved' ? 'right' : status === 'rejected' ? 'left' : 'down');

    try {
      await labService.validateSample(
        current.session_id,
        current.sample.id,
        { status, feedback: feedback.trim() || null }
      );
      setStats(prev => ({ ...prev, [status]: prev[status] + 1 }));
    } catch (err) {
      console.error('Error validating sample:', err);
    }

    // Advance after slide-out animation
    setTimeout(() => {
      setFeedback('');
      setCurrentIndex(prev => prev + 1);
      // Keep direction set so new card starts off-screen, then clear to animate in
      requestAnimationFrame(() => {
        setDirection(null);
        setSubmitting(false);
      });
    }, 300);
  }, [submitting, current, feedback]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!started || isFinished) return;

    const handleKeyDown = (e) => {
      // Don't trigger if typing in the feedback input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        if (e.key === 'Escape') {
          e.target.blur();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowRight':
        case 'l':
        case 'L':
          e.preventDefault();
          handleAction('approved');
          break;
        case 'ArrowLeft':
        case 'd':
        case 'D':
          e.preventDefault();
          handleAction('rejected');
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          e.preventDefault();
          handleAction('skipped');
          break;
        case 'Tab':
          e.preventDefault();
          document.getElementById('review-feedback')?.focus();
          break;
        case 'Escape':
          navigate('/admin/lab');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [started, isFinished, handleAction, navigate]);

  // ============================================
  // LOADING
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Carregando fila de review...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // START SCREEN
  // ============================================
  if (!started) {
    // Group by layout agent for breakdown
    const agentCounts = {};
    queue.forEach(item => {
      const agent = item.sample.layout_agent_name || item.sample.layout_type || 'unknown';
      agentCounts[agent] = (agentCounts[agent] || 0) + 1;
    });
    const sortedAgents = Object.entries(agentCounts).sort((a, b) => b[1] - a[1]);

    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FlaskConical className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Review de Posts</h1>
            <p className="text-gray-400">
              {queue.length === 0
                ? 'Nenhum post pendente de validacao'
                : `${queue.length} posts aguardando validacao`}
            </p>
          </div>

          {queue.length > 0 && (
            <>
              {/* Agent breakdown */}
              <div className="bg-gray-900 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-3">Por Layout Agent</h3>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {sortedAgents.map(([agent, count]) => (
                    <div key={agent} className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">{agent}</span>
                      <span className="text-amber-500 font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setStarted(true)}
                className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl text-lg transition-colors"
              >
                Iniciar Validacao
              </button>
            </>
          )}

          <button
            onClick={() => navigate('/admin/lab')}
            className="w-full mt-3 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Lab
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // FINISHED SCREEN
  // ============================================
  if (isFinished) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Review Concluido!</h1>
          <p className="text-gray-400 mb-6">{totalReviewed} posts avaliados</p>

          <div className="bg-gray-900 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">{stats.approved}</div>
                <div className="text-xs text-gray-400 mt-1">Aprovados</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">{stats.rejected}</div>
                <div className="text-xs text-gray-400 mt-1">Rejeitados</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-500">{stats.skipped}</div>
                <div className="text-xs text-gray-400 mt-1">Pulados</div>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              setStarted(false);
              setCurrentIndex(0);
              setStats({ approved: 0, rejected: 0, skipped: 0 });
              loadQueue();
            }}
            className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl transition-colors flex items-center justify-center gap-2 mb-3"
          >
            <RotateCcw className="w-4 h-4" />
            Recarregar Fila
          </button>

          <button
            onClick={() => navigate('/admin/lab')}
            className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Lab
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // REVIEW ACTIVE
  // ============================================
  const sample = current.sample;
  const config = current.session_config || {};

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* TOP BAR */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          {/* Row 1: Navigation + Progress */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => navigate('/admin/lab')}
              className="text-gray-400 hover:text-white flex items-center gap-1.5 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Sair
            </button>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">
                {totalReviewed + 1} / {queue.length}
              </span>
              <div className="w-32 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-300"
                  style={{ width: `${((totalReviewed) / queue.length) * 100}%` }}
                />
              </div>
              <button
                onClick={() => setShowShortcuts(!showShortcuts)}
                className="text-gray-500 hover:text-gray-300"
                title="Atalhos de teclado"
              >
                <Keyboard className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Keyboard shortcuts tooltip */}
          {showShortcuts && (
            <div className="mb-2 p-2 bg-gray-800 rounded-lg text-xs text-gray-400 flex gap-4 flex-wrap">
              <span><kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300">←</kbd> Dislike</span>
              <span><kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300">→</kbd> Like</span>
              <span><kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300">↓</kbd> Pular</span>
              <span><kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300">Tab</kbd> Feedback</span>
              <span><kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300">Esc</kbd> Sair</span>
            </div>
          )}

          {/* Row 2: Config context */}
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <span className="text-gray-400 break-words">
              {current.session_prompt}
            </span>
            <span className="h-3 w-px bg-gray-700 shrink-0" />

            {/* Layout agent badge */}
            {sample.layout_agent_name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">
                <Layers className="w-3 h-3 mr-1" />
                {sample.layout_agent_name}
              </span>
            )}

            {/* Colors */}
            {config.brand_colors?.length > 0 && (
              <span className="flex items-center gap-1">
                <Palette className="w-3 h-3 text-gray-500" />
                {config.brand_colors.map((c, i) => (
                  <span
                    key={i}
                    className="w-3.5 h-3.5 rounded-full border border-gray-600 inline-block"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </span>
            )}

            {/* Fonts */}
            {config.brand_fonts && (
              <span className="flex items-center gap-1 text-gray-500">
                <Type className="w-3 h-3" />
                {config.brand_fonts.primary}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* CENTER: Post Image */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div
          key={`${current.session_id}-${sample.id}`}
          className={`transition-all duration-300 ease-out ${
            direction === 'left' ? '-translate-x-full opacity-0' :
            direction === 'right' ? 'translate-x-full opacity-0' :
            direction === 'down' ? 'translate-y-full opacity-0' :
            'translate-x-0 opacity-100'
          }`}
        >
          {sample.image_url ? (
            <img
              src={sample.image_url}
              alt={sample.template_title || 'Post'}
              className="max-h-[65vh] max-w-full rounded-xl shadow-2xl object-contain"
            />
          ) : (
            <div className="w-80 h-80 bg-gray-800 rounded-xl flex items-center justify-center">
              <p className="text-gray-500 text-sm">Sem imagem</p>
            </div>
          )}

          {/* Layout type badge below image */}
          <div className="text-center mt-3">
            <span className="text-xs text-gray-500 font-mono">
              {sample.layout_type || sample.template_id}
            </span>
            {sample.generation_time_ms > 0 && (
              <span className="text-xs text-gray-600 ml-2">
                {(sample.generation_time_ms / 1000).toFixed(1)}s
              </span>
            )}
          </div>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className="bg-gray-900 border-t border-gray-800 px-4 py-4">
        <div className="max-w-lg mx-auto">
          {/* Feedback input */}
          <input
            id="review-feedback"
            type="text"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Feedback opcional (correcoes, observacoes...)"
            className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 mb-4"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAction('approved');
              }
            }}
          />

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => handleAction('rejected')}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              <ThumbsDown className="w-5 h-5" />
              Dislike
            </button>

            <button
              onClick={() => handleAction('skipped')}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-300 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              <SkipForward className="w-5 h-5" />
              Pular
            </button>

            <button
              onClick={() => handleAction('approved')}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 hover:text-green-300 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              <ThumbsUp className="w-5 h-5" />
              Like
            </button>
          </div>

          {/* Stats mini */}
          <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-600">
            <span className="text-green-600">{stats.approved} likes</span>
            <span className="text-red-600">{stats.rejected} dislikes</span>
            <span>{stats.skipped} pulados</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabReviewPage;
